class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.addEventListener('pointerdown', this.onSheetPointerDown.bind(this));
    this.addEventListener('pointermove', this.onSheetPointerMove.bind(this));
    this.addEventListener('pointerup', this.onSheetPointerEnd.bind(this));
    this.addEventListener('pointercancel', (event) => this.onSheetPointerEnd(event, true));
    this.addEventListener('click', this.onSheetHandleClick.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  isMobileSheet() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  resetSheetStyles() {
    const panel = this.querySelector('.drawer__inner');
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (!panel) return;
    panel.classList.remove('is-dragging');
    panel.style.removeProperty('transition');
    panel.style.removeProperty('transform');
    panel.style.removeProperty('height');
    panel.style.removeProperty('border-radius');
    overlay?.style.removeProperty('opacity');
  }

  onSheetPointerDown(event) {
    const handle = event.target.closest('.cart-drawer__drag-zone');
    if (!handle || !this.isMobileSheet() || !this.classList.contains('active') || event.button !== 0) return;

    const panel = this.querySelector('.drawer__inner');
    const rect = panel?.getBoundingClientRect();
    if (!panel || !rect) return;

    this.sheetPointerId = event.pointerId;
    this.sheetStartY = event.clientY;
    this.sheetDeltaY = 0;
    this.sheetStartHeight = rect.height;
    this.sheetStartTop = rect.top;
    this.sheetStartTime = performance.now();
    this.sheetDidDrag = false;
    handle.setPointerCapture(event.pointerId);
  }

  onSheetPointerMove(event) {
    if (event.pointerId !== this.sheetPointerId) return;
    const panel = this.querySelector('.drawer__inner');
    if (!panel) return;

    this.sheetDeltaY = event.clientY - this.sheetStartY;
    if (Math.abs(this.sheetDeltaY) < 3 && !this.sheetDidDrag) return;

    this.sheetDidDrag = true;
    panel.classList.add('is-dragging');

    if (this.sheetDeltaY >= 0) {
      panel.style.transform = `translate3d(0, ${this.sheetDeltaY}px, 0)`;
    } else if (!panel.classList.contains('is-expanded')) {
      const upwardDistance = Math.min(-this.sheetDeltaY, this.sheetStartTop);
      const expansionProgress = this.sheetStartTop ? Math.min(upwardDistance / this.sheetStartTop, 1) : 1;
      panel.style.transform = 'translate3d(0, 0, 0)';
      panel.style.height = `${Math.min(this.clientHeight, this.sheetStartHeight + upwardDistance)}px`;
      panel.style.borderRadius = `${26 * (1 - expansionProgress)}px ${26 * (1 - expansionProgress)}px 0 0`;
    }
  }

  onSheetPointerEnd(event, cancelled = false) {
    if (event.pointerId !== this.sheetPointerId) return;

    const handle = event.target.closest('.cart-drawer__drag-zone');
    if (handle?.hasPointerCapture(event.pointerId)) handle.releasePointerCapture(event.pointerId);

    const elapsed = Math.max(performance.now() - this.sheetStartTime, 1);
    const velocity = this.sheetDeltaY / elapsed;
    const panel = this.querySelector('.drawer__inner');
    const dismissDistance = Math.min(140, (panel?.offsetHeight || 600) * 0.22);
    const expandDistance = Math.min(120, Math.max(48, this.sheetStartTop * 0.4));
    const shouldDismiss = !cancelled && this.sheetDidDrag && (this.sheetDeltaY > dismissDistance || velocity > 0.65);
    const shouldExpand =
      !cancelled && this.sheetDidDrag && this.sheetDeltaY < -expandDistance && !panel?.classList.contains('is-expanded');

    this.sheetPointerId = undefined;
    if (!this.sheetDidDrag) return;

    this.sheetSuppressClick = true;
    setTimeout(() => {
      this.sheetSuppressClick = false;
    });

    if (shouldDismiss) {
      this.close();
    } else if (shouldExpand) {
      panel?.classList.add('is-expanded');
      this.resetSheetStyles();
    } else {
      this.resetSheetStyles();
    }
  }

  onSheetHandleClick(event) {
    if (!event.target.closest('.cart-drawer__drag-zone') || !this.isMobileSheet()) return;
    if (this.sheetSuppressClick) {
      event.preventDefault();
      return;
    }
    this.close();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.querySelector('.drawer__inner')?.classList.remove('is-expanded');
    this.resetSheetStyles();
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    this.querySelector('.drawer__inner')?.classList.remove('is-expanded', 'is-dragging');
    this.resetSheetStyles();
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
